import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../utils/api";
import "./Subscription.css";
import Footer from "./Footer";

const Subscription = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    // Fetch subscription plans and current subscription
    const fetchData = async () => {
      try {
        const [plansResponse, subscriptionResponse] = await Promise.all([apiService.getSubscriptionPlans(), apiService.getMySubscription()]);

        setPlans(plansResponse.data.plans);

        if (subscriptionResponse.data.hasActiveSubscription) {
          setCurrentSubscription(subscriptionResponse.data.subscription);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching subscription data:", err);
        setError("Failed to load subscription information. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      // Clean up script when component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    try {
      setProcessingPayment(true);

      // Create Razorpay order
      const orderResponse = await apiService.createRazorpayOrder(selectedPlan.id);
      const { order, key, user } = orderResponse.data;

      // Open Razorpay payment form
      const options = {
        key: key,
        amount: order.amount,
        currency: order.currency,
        name: "Pitman's Stenography",
        description: `${selectedPlan.name} Subscription`,
        order_id: order.id,
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.contact,
        },
        theme: {
          color: "#3399cc",
        },
        handler: async function (response) {
          try {
            // Verify payment and create subscription
            const verifyResponse = await apiService.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planType: selectedPlan.id,
              autoRenew: autoRenew,
            });

            // Update current subscription
            setCurrentSubscription(verifyResponse.data.subscription);
            setSelectedPlan(null);

            // Show success message
            alert("Subscription successful! You now have access to premium features.");
          } catch (err) {
            console.error("Payment verification failed:", err);
            alert("Payment verification failed. Please contact support.");
          } finally {
            setProcessingPayment(false);
          }
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (err) {
      console.error("Error initiating payment:", err);
      alert("Failed to initiate payment. Please try again later.");
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription?")) return;

    try {
      setLoading(true);
      await apiService.cancelSubscription();

      // Update subscription status
      const response = await apiService.getMySubscription();
      if (response.data.hasActiveSubscription) {
        setCurrentSubscription(response.data.subscription);
      } else {
        setCurrentSubscription(null);
      }

      alert("Subscription cancelled successfully.");
      setLoading(false);
    } catch (err) {
      console.error("Error cancelling subscription:", err);
      alert("Failed to cancel subscription. Please try again later.");
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="subscription-loading">Loading subscription information...</div>;
  }

  return (
    <div className="subscription-container">
      {error && <div className="error-message">{error}</div>}
      <h1>Subscription Plans</h1>

      {currentSubscription && (
        <div className="current-subscription">
          <h2>Your Current Subscription</h2>
          <div className="subscription-details">
            <p>
              <strong>Plan:</strong> {currentSubscription.planType.charAt(0).toUpperCase() + currentSubscription.planType.slice(1)}
            </p>
            <p>
              <strong>Amount:</strong> {currentSubscription.amount}
            </p>
            <p>
              <strong>Status:</strong> {currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1)}
            </p>
            <p>
              <strong>Start Date:</strong> {new Date(currentSubscription.startDate).toLocaleDateString()}
            </p>
            <p>
              <strong>End Date:</strong> {new Date(currentSubscription.endDate).toLocaleDateString()}
            </p>
            <p>
              <strong>Auto-Renew:</strong> {currentSubscription.autoRenew ? "Yes" : "No"}
            </p>
          </div>
        </div>
      )}

      <div className="subscription-plans">
        <h2>{currentSubscription ? "" : "Choose a Plan"}</h2>
        <div className="plans-grid">
          {!currentSubscription &&
            plans.map((plan) => (
              <div key={plan.id} className={`plan-card ${selectedPlan?.id === plan.id ? "selected" : ""}`} onClick={() => handlePlanSelect(plan)}>
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="plan-price">
                    <span className="currency">₹</span>
                    <span className="amount">{plan.price}</span>
                    <span className="duration">/{plan.duration}month</span>
                  </div>
                </div>

                <div className="plan-features">
                  <ul>
                    {plan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
        </div>

        {selectedPlan && (
          <div className="subscription-actions">
            <label className="auto-renew-checkbox">
              <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
              Auto-renew subscription (can be cancelled anytime)
            </label>

            <button className="subscribe-btn" onClick={handleSubscribe} disabled={processingPayment}>
              {processingPayment ? "Processing..." : `Subscribe to ${selectedPlan.name}`}
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Subscription;
